import { useTheme } from '@/components/ThemeProvider';
import { Menu, Moon, Sun, X } from "lucide-react";
import { useEffect, useState } from 'react';
import { FaLeaf } from 'react-icons/fa';
import DevSettings from '../DevSettings/DevSettings';

const Navbar = ({ sidebarToggle, setSidebarToggle }) => {
   const [brightness, setBrightness] = useState(() => {
      const savedBrightness = localStorage.getItem('brightness');
      return savedBrightness ? parseInt(savedBrightness, 10) : 100;
   });
   const [typewriterEnabled, setTypewriterEnabled] = useState(() => {
      const saved = localStorage.getItem('typewriterEnabled');
      return saved === null ? true : saved === 'true';
   });
   const [showDevSettings, setShowDevSettings] = useState(() => {
      const saved = localStorage.getItem('showDevSettings');
      return saved === 'true';
   });
   const { theme, setTheme } = useTheme();

   // Handle keyboard shortcut for dev settings (Ctrl+Shift+D)
   useEffect(() => {
      const handleKeyDown = (event) => {
         if (event.ctrlKey && event.shiftKey && event.key === 'D') {
            event.preventDefault();
            setShowDevSettings(prev => {
               const newValue = !prev;
               localStorage.setItem('showDevSettings', newValue.toString());
               return newValue;
            });
         }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
   }, []);

   useEffect(() => {
      document.documentElement.style.filter = `brightness(${brightness}%)`;
      localStorage.setItem('brightness', brightness.toString());
      return () => {
         document.documentElement.style.filter = 'brightness(100%)';
      };
   }, [brightness]);

   useEffect(() => {
      localStorage.setItem('typewriterEnabled', typewriterEnabled.toString());
   }, [typewriterEnabled]);

   const handleToggle = () => {
      setSidebarToggle(!sidebarToggle);
   };

   const handleThemeToggle = () => {
      const newTheme = theme === "light" ? "dark" : "light";
      setTheme(newTheme);
      localStorage.setItem('theme', newTheme);
   };

   return (
      <nav className="fixed top-0 left-0 right-0 z-20 bg-background border-b shadow-sm">
         <div className="flex justify-between items-center h-16">
            {/* Left Section - align with sidebar */}
            <div className="flex items-center gap-4 pl-4">
               <button
                  onClick={handleToggle}
                  className="text-foreground hover:text-green-700 p-2 rounded-lg
                            hover:bg-accent transition-colors duration-200 flex items-center justify-center"
                  aria-label="Toggle Sidebar"
               >
                  <div className="relative w-5 h-5">
                     <Menu
                        size={20}
                        className={`absolute transition-all duration-200
                                    ${sidebarToggle ? 'opacity-0 rotate-90' : 'opacity-100 rotate-0'}`}
                     />
                     <X
                        size={20}
                        className={`absolute transition-all duration-200
                                    ${sidebarToggle ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-90'}`}
                     />
                  </div>
               </button>
               <div className="flex items-center gap-3">
                  <FaLeaf className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <span className="text-xl font-bold bg-gradient-to-r from-green-800 to-green-600 dark:from-green-400 dark:to-green-600 bg-clip-text text-transparent">
                     PermitTree
                  </span>
               </div>
            </div>

            {/* Center Section */}
            <div className="flex items-center">
               {showDevSettings && (
                  <DevSettings
                     brightness={brightness}
                     setBrightness={setBrightness}
                     typewriterEnabled={typewriterEnabled}
                     setTypewriterEnabled={setTypewriterEnabled}
                     setTheme={setTheme}
                  />
               )}
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-4 pr-4">
               <button
                  onClick={handleThemeToggle}
                  className="w-9 h-9 rounded-lg flex items-center justify-center border border-input bg-background hover:bg-accent hover:text-accent-foreground"
               >
                  <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                  <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                  <span className="sr-only">Toggle theme</span>
               </button>
               <div className="text-sm font-medium text-foreground">
                  DENR Region 4B
               </div>
            </div>
         </div>
      </nav>
   );
};

export default Navbar;
